import React from "react";
import Slider from "react-slick";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const ArrowButton = ({ onClick, direction }) => (
  <button
    type="button"
    aria-label={direction === "left" ? "Previous slide" : "Next slide"}
    onClick={onClick}
    className={`cs-arrow cs-arrow-${direction}`}
  >
    {direction === "left" ? <ChevronLeftIcon /> : <ChevronRightIcon />}
  </button>
);

const CarouselSlider = ({
  slides = [],
  height = 200,
  width = "100%",
  showNavigator = true,
  duration = 3,
  showRoundedCorner = true,
  activeDotColor = "#ffffff",
  onSlideClick = () => {},
}) => {
  const slideHeight = Number(height) > 0 ? Number(height) : 200;
  let enabledSlides = slides.filter((s) => s.enabled && s.foreground);
  let sliderSlides = enabledSlides;

  if (
    enabledSlides.length === 1 &&
    enabledSlides[0]?.foreground?.structure === "multi_media"
  ) {
    const media = enabledSlides[0].foreground.media || [];

    if (media.length > 1) {
      sliderSlides = media.map((m) => ({
        ...enabledSlides[0],
        foreground: {
          ...enabledSlides[0].foreground,
          media: [m], // only 1 media per slide
        },
      }));
    }
  }
  const MEDIA_STYLE = {
    width: "100%",
    height: slideHeight,
    objectFit: "cover",
    display: "block",
  };
  // const VIDEO_STYLE = {
  //   ...MEDIA_STYLE,
  //   marginLeft: "80px",
  // };
  // const IMAGE_STYLE = {
  //   ...MEDIA_STYLE,
  //   marginLeft: "0px",
  // };

  if (enabledSlides.length === 0) {
    enabledSlides.push({
      enabled: true,
      foreground: {
        structure: "images",
        images: ["/images/gold_price_table.png"],
      },
    });
  }

  const hasMany = sliderSlides.length > 1;

  const settings = {
    dots: showNavigator && hasMany,
    infinite: hasMany,
    autoplay: hasMany,
    autoplaySpeed: duration * 1000,
    speed: 500,
    arrows: hasMany,
    prevArrow: <ArrowButton direction="left" />,
    nextArrow: <ArrowButton direction="right" />,
    centerMode: hasMany,
    centerPadding: "20%",
    slidesToShow: 1,
    slidesToScroll: 1,
    focusOnSelect: true,
    pauseOnHover: false,
    pauseOnFocus: false,
    pauseOnDotsHover: false,
    customPaging: () => <span className="cs-dot" />,
    appendDots: (dots) => <ul className="cs-dots">{dots}</ul>,
    responsive: [
      { breakpoint: 900, settings: { centerPadding: "14%" } },
      { breakpoint: 600, settings: { centerPadding: "9%" } },
    ],
  };

  const getFullImage = (img) => {
    if (!img || typeof img !== "string") {
      return "/images/gold_price_table.png";
    }
    return img.startsWith("http")
      ? img
      : `${process.env.REACT_APP_CDN_URL}/${process.env.REACT_APP_PACKAGE_NAME}/${img}`;
  };

  const radius = showRoundedCorner ? 20 : 0;

  return (
    <div className="app-carousel" style={{ width, margin: "auto", position: "relative" }}>
      <style>
        {`
          .app-carousel .slick-list {
            height: ${slideHeight}px !important;
             padding-top: 18px !important;
            padding-bottom: 18px !important;
          }
          .app-carousel .slick-track {
            max-width: none !important;
          }
          .app-carousel .slick-slide,
          .app-carousel .slick-slide > div {
            height: ${slideHeight}px !important;
          }
          .app-carousel .slick-slide > div {
            padding: 0 8px;
            box-sizing: border-box;
          }
          .app-carousel .slick-slide > div > div {
            height: ${slideHeight}px;
            border-radius: ${radius}px !important;
            overflow: hidden;
          }
          .app-carousel .slick-slide img,
          .app-carousel .slick-slide video {
            width: 100%;
            height: ${slideHeight}px !important;
            object-fit: cover;
          }
            /* side slides: smaller + faded */
          .app-carousel .slick-slide {
            transform: scale(0.82);
            opacity: 0.45;
            filter: grayscale(0.4);
            transition: transform 0.4s ease, opacity 0.4s ease, filter 0.4s ease;
          }
          .app-carousel .slick-slide.slick-center {
            transform: scale(1);
            opacity: 1;
            filter: none;
          }
          .app-carousel .slick-slide.slick-center > div > div {
            box-shadow: 0 14px 28px rgba(0, 0, 0, 0.35);
          }

          /* arrows */
          .app-carousel .cs-arrow {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            z-index: 5;
            width: 44px;
            height: 44px;
            border: none;
            border-radius: 50%;
            background: rgba(60, 70, 90, 0.85);
            color: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            padding: 0;
          }
          .app-carousel .cs-arrow:hover { background: rgba(60, 70, 90, 1); }
          .app-carousel .cs-arrow-left { left: 8px; }
          .app-carousel .cs-arrow-right { right: 8px; }
          .app-carousel .cs-arrow::before { content: none !important; }
          @media (max-width: 600px) {
            .app-carousel .cs-arrow { display: none !important; }
          }

          /* dots */
          .app-carousel .cs-dots {
            position: static;
            display: flex !important;
            justify-content: center;
            gap: 8px;
            padding: 0;
            margin: 6px 0 0;
            list-style: none;
          }
          .app-carousel .cs-dots li {
            width: auto;
            height: auto;
            margin: 0;
          }
          .app-carousel .cs-dot {
            display: block;
            width: 10px;
            height: 10px;
            border-radius: 5px;
            background: rgba(255, 255, 255, 0.35);
            transition: width 0.3s ease, background 0.3s ease;
          }
          .app-carousel .cs-dots li.slick-active .cs-dot {
            width: 24px;
            background: ${activeDotColor};
          }
        `}
      </style>

      <Slider {...settings}>
        {sliderSlides.map((item, index) => {
          const borderRadius = showRoundedCorner ? 10 : 0;

          const backgroundStyle =
            item.background?.colors?.length > 0
              ? {
                  background: `linear-gradient(${
                    item.background.direction === "LR" ? "90deg" : "180deg"
                  }, ${item.background.colors.join(", ")})`,
                }
              : { background: "transparent" };

          const fg = item.foreground || {};

          switch (fg.structure) {
            /* ================= MULTI MEDIA ================= */
            case "multi_media": {
              const mediaList = fg.media || [];
              if (mediaList.length === 0) return null;

              const renderMedia = (media, i) =>
                media.mediaType === "video" ? (
                  <video
                    key={i}
                    src={getFullImage(media.path)}
                    autoPlay
                    muted
                    loop
                    playsInline
                    style={MEDIA_STYLE}
                  />
                ) : (
                  <img
                    key={i}
                    src={getFullImage(media.path)}
                    alt=""
                    style={MEDIA_STYLE}
                    onError={(e) =>
                      (e.target.src = "/images/gold_price_table.png")
                    }
                  />
                );

              if (mediaList.length === 1) {
                return (
                  <div
                    key={index}
                    onClick={() => onSlideClick(item)}
                    style={{
                      height: slideHeight,
                      borderRadius,
                      overflow: "hidden",
                      ...backgroundStyle,
                    }}
                  >
                    {renderMedia(mediaList[0], 0)}
                  </div>
                );
              }

              const getGridStyle = (count) => {
                if (count === 2) return { gridTemplateColumns: "1fr 1fr" };
                if (count === 3) return { gridTemplateColumns: "2fr 1fr" };
                return { gridTemplateColumns: "1fr 1fr" };
              };

              return (
                <div
                  key={index}
                  onClick={() => onSlideClick(item)}
                  style={{
                    height: slideHeight,
                    borderRadius,
                    overflow: "hidden",
                    padding: 8,
                    ...backgroundStyle,
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      height: "100%",
                      gap: 10,
                      ...getGridStyle(mediaList.length),
                    }}
                  >
                    {mediaList.map((media, i) => (
                      <div
                        key={i}
                        style={{
                          borderRadius: 12,
                          overflow: "hidden",
                          gridRow:
                            mediaList.length === 3 && i === 0
                              ? "1 / span 2"
                              : "auto",
                        }}
                      >
                        {renderMedia(media, i)}
                      </div>
                    ))}
                  </div>
                </div>
              );
            }

            /* ================= IMAGES ================= */
            case "images":
              return (
                <div
                  key={index}
                  onClick={() => onSlideClick(item)}
                  style={{
                    borderRadius,
                    overflow: "hidden",
                    ...backgroundStyle,
                  }}
                >
                  <div style={{ display: "flex", gap: 4 }}>
                    {(fg.images || []).map((img, i) => (
                      <img
                        key={i}
                        src={getFullImage(img)}
                        alt=""
                        style={MEDIA_STYLE}
                        onError={(e) =>
                          (e.target.src = "/images/gold_price_table.png")
                        }
                      />
                    ))}
                  </div>
                </div>
              );

            /* ================= TEXTS ================= */
            case "texts":
              return (
                <div
                  key={index}
                  onClick={() => onSlideClick(item)}
                  style={{
                    borderRadius,
                    padding: 20,
                    ...backgroundStyle,
                  }}
                >
                  {(fg.texts || []).map((txt, i) => {
                    const baseSize = 18;
                    const baseMargin = 500;

                    const calculatedMargin =
                      baseMargin + (baseSize - (txt.size || 18)) * 12.5;

                    return (
                      <p
                        key={i}
                        style={{
                          margin: "10px 0",
                          marginLeft: `${calculatedMargin}px`,
                          color: txt.color || "#000",
                          fontSize: txt.size || 18,
                          fontWeight: "bold",
                          textAlign: "justify",
                        }}
                      >
                        {txt.text}
                      </p>
                    );
                  })}
                </div>
              );

            /* ================= IMAGE + TEXT ================= */
            case "image_text":
            case "text_image":
              return (
                <div
                  key={index}
                  onClick={() => onSlideClick(item)}
                  style={{
                    borderRadius,
                    overflow: "hidden",
                    display: "flex",
                    ...backgroundStyle,
                  }}
                >
                  {(fg.structure === "text_image"
                    ? ["text", "image"]
                    : ["image", "text"]
                  ).map((type, i) =>
                    type === "image" ? (
                      <div key={i} style={{ flex: 1 }}>
                        {(fg.images || []).map((img, j) => (
                          <img
                            key={j}
                            src={getFullImage(img)}
                            alt=""
                            style={MEDIA_STYLE}
                          />
                        ))}
                      </div>
                    ) : (
                      <div
                        key={i}
                        style={{
                          flex: 1,
                          padding: 16,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {(fg.texts || []).map((txt, j) => (
                          <p
                            key={j}
                            style={{
                              color: txt.color || "#000",
                              fontSize: txt.size || 14,
                            }}
                          >
                            {txt.text}
                          </p>
                        ))}
                      </div>
                    ),
                  )}
                </div>
              );

            default:
              return null;
          }
        })}
      </Slider>
    </div>
  );
};

export default CarouselSlider;
